/**
 * Reads the application key out of whatever Superchat gave Ops to paste.
 *
 * Superchat hands over a snippet, not a key:
 *
 *   <script src='https://widget.superchat.de/snippet.js?applicationKey=WC6d...'
 *           referrerpolicy='no-referrer-when-downgrade'></script>
 *
 * Asking someone to pick the key out of that by hand is asking them to do a
 * parsing job, and to get it right every time. So the field takes the snippet.
 *
 * What is *stored* is still only the key. The snippet is never kept and never
 * injected as markup: a settings field whose contents become HTML would let
 * anyone with an admin session put arbitrary script on the sign-in page. The
 * widget always loads a fixed widget.superchat.de URL built from the key, so the
 * worst a bad paste can do is point at the wrong Superchat workspace.
 */

/** The only host a key may come from. A snippet pointing anywhere else is refused. */
export const SUPERCHAT_HOST = 'widget.superchat.de';

const KEY_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

export type SnippetParse =
	| { ok: true; applicationKey: string; source: 'key' | 'url' | 'snippet' }
	| { ok: false; reason: string };

const fromUrl = (raw: string): SnippetParse | null => {
	let url: URL;
	try {
		url = new URL(raw);
	} catch {
		return null;
	}
	if (url.hostname.toLowerCase() !== SUPERCHAT_HOST) {
		return {
			ok: false,
			reason: `That points at ${url.hostname}, not ${SUPERCHAT_HOST}. Paste the snippet Superchat gave you.`
		};
	}
	const key = url.searchParams.get('applicationKey');
	if (!key) {
		return { ok: false, reason: 'That Superchat URL has no applicationKey in it.' };
	}
	return KEY_PATTERN.test(key)
		? { ok: true, applicationKey: key, source: 'url' }
		: { ok: false, reason: 'The applicationKey in that URL does not look like a key.' };
};

/**
 * Accepts a bare key, a snippet URL, or the whole script tag. Anything else is
 * refused with a reason rather than silently stored.
 */
export const parseSuperchatInput = (input: string): SnippetParse => {
	const trimmed = (input || '').trim();
	if (!trimmed) {
		return { ok: false, reason: 'Paste the snippet from Superchat, or the application key.' };
	}

	// A bare key: the common case once someone has done this before.
	if (KEY_PATTERN.test(trimmed)) {
		return { ok: true, applicationKey: trimmed, source: 'key' };
	}

	// A script tag. Pull the src out rather than parsing HTML — the string is never
	// rendered, so there is nothing to sanitise, only something to read.
	const src = /<script[^>]*\ssrc\s*=\s*['"]([^'"]+)['"]/i.exec(trimmed);
	if (src) {
		const parsed = fromUrl(src[1]);
		if (parsed) {
			return parsed.ok ? { ...parsed, source: 'snippet' } : parsed;
		}
		return { ok: false, reason: 'That script tag has no usable Superchat URL in it.' };
	}

	// A bare URL, pasted without the surrounding tag.
	const asUrl = fromUrl(trimmed);
	if (asUrl) {
		return asUrl;
	}

	if (trimmed.includes('<script')) {
		return { ok: false, reason: 'That looks like a script tag, but it has no src attribute.' };
	}
	return {
		ok: false,
		reason: 'That is not a Superchat snippet or application key. Paste the whole snippet from Superchat.'
	};
};

/**
 * The canonical form of a snippet for a given key.
 *
 * What Ops paste arrives however their clipboard left it — one long line, odd
 * indentation, single quotes, stray blank lines. Tidying it on the way in means
 * the field always shows the same shape, so the only thing that looks different
 * between two snippets is the part that actually differs: the key.
 *
 * Built from the key rather than from the pasted text, so what is displayed is
 * exactly what will be loaded — not the attributes someone happened to paste.
 */
export const snippetLines = (applicationKey: string): string[] => {
	if (!applicationKey) {
		return [];
	}
	return [
		'<script',
		// Two spaces, not a tab: a textarea renders tabs eight columns wide and the
		// snippet ends up wrapping for no reason.
		`  src="https://${SUPERCHAT_HOST}/snippet.js?applicationKey=${applicationKey}"`,
		'  referrerpolicy="no-referrer-when-downgrade"',
		'></script>'
	];
};

export const formatSuperchatSnippet = (applicationKey: string): string => snippetLines(applicationKey).join('\n');

/**
 * How tall the field should stand, in lines.
 *
 * Derived from the snippet rather than fixed, so adding or dropping an attribute
 * changes the field with it. Falls back to the shape of a real snippet while the
 * field is empty, so it does not open as a single line and jump once something
 * is pasted in.
 */
export const snippetRowCount = (value: string): number => {
	const typed = value.trim() ? value.split('\n').length : 0;
	return Math.max(typed, snippetLines('x'.repeat(8)).length);
};

/**
 * Sizing the snippet so it fits on one line each.
 *
 * The src line is long and its length depends on the key, so at a fixed size it
 * either wraps or scrolls sideways. Both hurt: a wrapped snippet no longer looks
 * like the thing Superchat showed, and a sideways scroll hides the end of the
 * URL — which is exactly where the key is.
 *
 * Shrinking the type instead keeps every line whole and the shape recognisable.
 * A monospace advance is a reliable fraction of the font size, so the fit can be
 * computed rather than measured glyph by glyph.
 */
const MONO_ADVANCE = 0.6;
/** Room for the line-number gutter, in characters. */
const GUTTER_COLUMNS = 4;
export const SNIPPET_FONT_PX = { min: 8.5, max: 13 };

export const longestLineLength = (text: string): number =>
	text.split('\n').reduce((longest, line) => Math.max(longest, line.length), 0);

export const snippetFontSizePx = (availableWidth: number, longestLine: number): number => {
	// Before the block is measured, and for an empty snippet, full size is the
	// right answer — shrinking to fit nothing would be a strange first impression.
	if (!(availableWidth > 0) || !(longestLine > 0)) {
		return SNIPPET_FONT_PX.max;
	}
	const fitted = availableWidth / ((longestLine + GUTTER_COLUMNS) * MONO_ADVANCE);
	// A floor as well as a ceiling: past a point the snippet is unreadable, and a
	// key long enough to hit it is better scrolled than reduced to a grey smear.
	return Math.min(SNIPPET_FONT_PX.max, Math.max(SNIPPET_FONT_PX.min, fitted));
};
